import { useCallback, useRef, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import { breakpoints, Inline, SiteAuxiliaryButton, Text, vars } from '@etg/wings';
import { useAuth } from '@eti/authentication';
import { useDirection, useProperty, useSiteContext, useTranslation } from '@eti/providers';
import { AuthenticationType } from '@eti/schema-types';
import { css, cx } from '@eti/styles';
import { useApolloClient } from '@apollo/client';
import { CaretDown, SignOut, User } from '@phosphor-icons/react';
import { observeClickOutside } from 'react-ref-observe';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { removeExtraProductsFromPBSession } from '@eti/products';
import { routes } from '../../../constants/routesAndApisConstants';
import { useCloseOnEsc } from '../../../utils/useCloseOnEsc';
import { useCloseOnFocusOut } from '../../../utils/useCloseOnFocusOut';
import { useNavigateOnArrowKeys } from '../../../utils/useNavigateOnArrowKeys';
import { asyncInit } from '../../../utils/facebook';
import { useChat } from '../../chat/useChat';
import { CONNECTED, SUCCESS } from '../constants';
import MyBookingsModal from '../containers/MyBookingsModal';
import DropdownMenu from '../../../widgets/dropdown-menu/DropdownMenu';

const wrapperStyles = css`
  position: relative;

  @media print {
    display: none;
  }
`;

const getDropdownStyles = (hiddenLabel?: boolean) => css`
  margin-top: 3px;
  min-width: 170px;
  ${hiddenLabel ? `inset-inline-end: 0` : `inset-inline-start: 0`};
  width: auto;
  z-index: 2;

  @media (min-width: ${breakpoints._970}) {
    width: 100%;
  }
`;

const getUserIconStyles = (hiddenLabel: boolean) => css`
  position: relative;

  &::before {
    background-color: ${vars.colors.signal.positive.foreground};
    border: 2px solid ${vars.colors.button.siteAuxiliary.default.base};
    border-radius: 50%;
    content: '';
    display: block;
    height: 0.625rem;
    inset-inline-start: calc(
      ${hiddenLabel ? vars.button.blockPadding.smallWithIcon : '1.5rem'} + 0.625rem
    );
    margin-bottom: 0.75rem;
    position: absolute;
    width: 0.625rem;
  }

  &:hover::before {
    border-color: ${vars.colors.button.siteAuxiliary.hover.base};
  }

  &:active::before {
    border-color: ${vars.colors.button.siteAuxiliary.active.base};
  }
`;

const getCaretStyles = (isOpen: boolean) => css`
  flex-shrink: 0;
  transform: rotateX(${isOpen ? '180' : '0'}deg);
  transition: transform 0.15s ease-in-out;
`;

const routesToRedirectToOrderLogin = [
  routes.ORDER,
  routes.ORDER_STATUS,
  routes.ORDER_LIST,
  routes.ORDER_DETAILS,
  routes.CHANGE_TRIP,
];

interface MyBookingsButtonProps {
  className?: string;
  'data-testid'?: string;
  shouldHideLabel?: boolean | null;
}

const MyBookingsButton = ({
  className,
  'data-testid': dataTestId = 'myBookingsButton-header',
  shouldHideLabel = null,
}: MyBookingsButtonProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const history = useHistory();
  const { isRTL } = useDirection();
  const { pathname } = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const dropdownItemRef = useRef<HTMLAnchorElement | null>(null);
  const {
    language: { code: languageCode },
    market: { code: marketCode },
  } = useSiteContext();

  const { user, logoutUser } = useAuth();
  const { closeChat } = useChat();
  const client = useApolloClient();
  const isWiderViewport = useMediaQuery(`(min-width: ${breakpoints._970})`);

  const isMyBookingsButtonEnabled = !p('IbeClient.HideMyBookingsButton.Enabled');
  const isMyBookingsModalEnabled = p('IbeClient.MyBookingsModal.Enabled');
  const facebookClientID = p('IbeClient.MyBookings.Facebook.Client.Id');

  const closeDropdown = () => {
    setIsDropdownOpen(false);
  };

  observeClickOutside([wrapperRef], closeDropdown);
  useCloseOnEsc(dropdownRef, closeDropdown, buttonRef);
  useCloseOnFocusOut(wrapperRef, closeDropdown);
  const { handleKeyDown } = useNavigateOnArrowKeys({
    buttonRef,
    dropdownRef,
    isSubMenuOpen: isDropdownOpen,
  });

  const isPostbookingProductsPage = pathname.includes(routes.POSTBOOKING_PRODUCTS);
  const isAuthenticationPage =
    pathname === routes.ORDER_AUTHENTICATION ||
    pathname === routes.POSTBOOKING_AUTHENTICATION ||
    pathname === routes.RYANAIR_ORDER_AUTHENTICATION;

  const redirectToStartPage = () => {
    history.push(routes.START);
  };

  const redirectToLoginPage = () => {
    history.push(routes.ORDER_AUTHENTICATION);
  };

  const cleanupAndRedirect = async () => {
    closeChat();

    if (routesToRedirectToOrderLogin.some((route) => pathname.includes(route))) {
      if (isMyBookingsModalEnabled) {
        redirectToStartPage();
      } else {
        redirectToLoginPage();
      }
    }

    if (isPostbookingProductsPage) {
      history.push(routes.POSTBOOKING_AUTHENTICATION);
    }
    await client.clearStore();
  };

  const handleClick = () => {
    if (!user) {
      if (isMyBookingsModalEnabled && !isAuthenticationPage) {
        setIsLoginModalOpen(true);
      } else {
        redirectToLoginPage();
      }
    } else if (isDropdownOpen) {
      closeDropdown();
    } else {
      setIsDropdownOpen(true);
    }
  };

  const facebookLogout = () => {
    asyncInit(facebookClientID, languageCode, marketCode, () => {
      window.FB.getLoginStatus((response) => {
        if (response.status === CONNECTED) {
          window.FB.logout(() => {
            cleanupAndRedirect();
          });
        } else {
          cleanupAndRedirect();
        }
      });
    });
  };

  const handleLogout = async () => {
    closeDropdown();

    try {
      const { data: logoutData } = await logoutUser();

      if (logoutData?.logoutUser?.response === SUCCESS) {
        removeExtraProductsFromPBSession();
        if (user?.loginType === AuthenticationType.Facebook) {
          facebookLogout();
        } else {
          cleanupAndRedirect();
        }
      }
    } catch (error) {
      console.error('Error during logout:', error); // oxlint-disable-line no-console
      throw error;
    }
  };

  const handleLoginModalDismiss = useCallback(() => {
    setIsLoginModalOpen(false);
  }, []);

  const elementType = user || (isMyBookingsModalEnabled && !isAuthenticationPage) ? 'button' : Link;
  const isLabelHidden = shouldHideLabel !== null ? shouldHideLabel : !isWiderViewport;
  const menuId = 'my-bookings-menu';

  return (
    <div className={wrapperStyles} data-testid="my-bookings-button-header" ref={wrapperRef}>
      {isMyBookingsButtonEnabled && (
        <SiteAuxiliaryButton
          aria-controls={user ? menuId : undefined}
          aria-expanded={user ? isDropdownOpen : undefined}
          aria-label={!user ? t('Postbooking.Login.Header.Title') : undefined}
          as={elementType}
          className={cx(className, user && getUserIconStyles(isLabelHidden))}
          data-testid={dataTestId}
          hideLabel={isLabelHidden}
          iconPrefix={<User aria-hidden />}
          iconSuffix={
            user ? <CaretDown aria-hidden className={getCaretStyles(isDropdownOpen)} /> : undefined
          }
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          ref={buttonRef}
          size="large"
          {...(elementType === Link ? { to: routes.ORDER_AUTHENTICATION } : {})}
        >
          {t('SiteHeader.My.Bookings.Button')}
        </SiteAuxiliaryButton>
      )}
      {isDropdownOpen && (
        <DropdownMenu
          aria-hidden={!isDropdownOpen}
          className={getDropdownStyles(isLabelHidden)}
          data-testid={menuId}
          id={user ? menuId : undefined}
          ref={dropdownRef}
        >
          <DropdownMenu.Item>
            <Inline
              as={Link}
              data-testid="myBookings-see-order"
              onClick={closeDropdown}
              onKeyDown={handleKeyDown}
              ref={dropdownItemRef}
              to={routes.ORDER_LIST}
            >
              <Text>{t('Postbooking.Show.Bookings.Button')}</Text>
            </Inline>
          </DropdownMenu.Item>
          <DropdownMenu.Item>
            <Inline
              as="button"
              data-testid="myBookings-logout"
              onClick={handleLogout}
              onKeyDown={handleKeyDown}
              spacing={8}
            >
              <SignOut aria-hidden mirrored={!isRTL} size={18} weight="regular" />
              <Text>{t('Postbooking.Logout.Button')}</Text>
            </Inline>
          </DropdownMenu.Item>
        </DropdownMenu>
      )}
      <MyBookingsModal isLoginModalOpen={isLoginModalOpen} onDismiss={handleLoginModalDismiss} />
    </div>
  );
};

export default MyBookingsButton;
